import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Check, Copy, Share2, Zap, Activity, ShieldCheck, GraduationCap, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";
import { accountUrl, goToAccount } from "@/lib/account";
import { CornerFrame } from "@/components/landing/CornerFrame";

const SHARE_BASE = "https://www.tradrquant.com";

const STATS = [
  ["800+", "assets scanned"],
  ["24", "modules"],
  ["16", "strategies"],
];

const BOOT = [
  ["boot", "tradrquant_os v1.0", "OK"],
  ["load", "market_intelligence layer", "OK"],
  ["sync", "whale + order-flow feeds", "LIVE"],
  ["auth", "founder_access", "OPEN"],
];

const BENEFITS = [
  { icon: Zap, t: "premium_indicator", d: "The non-repainting TradrQuant Pro confluence tool — free." },
  { icon: Activity, t: "whale_flow_alerts", d: "Live large-wallet moves + order flow, pushed to Telegram." },
  { icon: ShieldCheck, t: "verified_ledger", d: "Every resolved signal published — wins and losses." },
  { icon: GraduationCap, t: "trader_academy", d: "Nine interactive chapters, candlesticks to confluence." },
];

const STEPS = [
  ["01", "Create your free account", "Sign up on the TradrQuant terminal in seconds. No card, no commitment."],
  ["02", "Unlock the free tools", "Get the Pro indicator, whale + flow alert channels and the Academy today, while you wait."],
  ["03", "Get early access", "When the founder terminal opens, free account holders get in first — at founder pricing, before public launch."],
];

const QUOTES = [
  ["The free indicator alone is better than tools I paid $90/mo for.", "@chartsniper"],
  ["First alert service where I can actually check the losing trades too.", "@degen_dan"],
  ["Whale + flow feed on Telegram is genuinely fast. No fluff.", "@sol_maxi"],
];

const FAQ = [
  ["Is it really free?", "Yes. The Pro indicator, whale + pattern alert channels and the chart-reading Academy are free today — no card required. The full terminal is a paid product opening in founder early access."],
  ["What do I get for joining now?", "Immediate access to the free tools, plus a locked-in spot at the front of the queue for the founder terminal at founder pricing before public launch."],
  ["Is it really free?", "Yes. Creating an account is free and needs no card. The public order flow terminal and free tools are yours today."],
  ["Is this financial advice?", "No. TradrQuant is market-intelligence and education tooling for retail traders. Crypto trading carries risk and outcomes are never guaranteed."],
];

const COMPARE = [
  ["Premium confluence indicator", "$40–90/mo", "Free"],
  ["Whale + order-flow alerts", "$50–100/mo", "Free"],
  ["Structured trading course", "$100–300", "Free"],
  ["Verified win/loss record", "Hidden", "Published"],
];

const MARQUEE = ["Screener", "Whale Flow", "Confluence Signals", "Liquidation Map", "Verified Ledger", "Order Flow", "GEX / Gamma", "Academy"];

const TOOLS = [
  { name: "Order Flow Terminal", tag: "terminal", desc: "Footprint, Smart Tape, CVD & the Hyperliquid whale layer. BTC & ETH, free.", href: "https://alpha.tradrquant.com/terminal" },
  { name: "Whale Radar", tag: "radar", desc: "Positioning, capital & liquidation risk in real time.", href: "https://radar.tradrquant.com/" },
  { name: "Alpha Flow", tag: "alpha", desc: "Live order flow & market microstructure.", href: "https://alpha.tradrquant.com" },
  { name: "Verify", tag: "verify", desc: "Every resolved signal — wins and losses, published.", href: "https://verify.tradrquant.com" },
  { name: "TradrQuant Pro", tag: "indicator", desc: "The free confluence indicator, on TradingView.", href: "https://www.tradingview.com/script/MiXDrieo-TradrQuant-Pro/" },
];

const fade = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function CountUp({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  const m = String(value).match(/^(\d+)(.*)$/);
  const target = m ? parseInt(m[1], 10) : 0;
  const suffix = m ? m[2] : "";
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1300;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return <span ref={ref}>{n}{suffix}</span>;
}

export default function JoinFunnel() {
  useSeo({
    title: "Join TradrQuant — Founder Early Access | Pro Crypto Trading Terminal",
    description:
      "Create your free TradrQuant account. Free premium indicator, live whale + flow alerts, a verified track record and the chart-reading Academy — professional crypto market intelligence for retail traders.",
    path: "/join",
  });

  const status = "idle";
  const dup = false;
  const refCode = "";
  const [copied, setCopied] = useState(false);

  const shareLink = refCode ? `${SHARE_BASE}/?ref=${refCode}` : SHARE_BASE;
  const shareText =
    "I just joined TradrQuant — the professional crypto trading terminal for retail. Free tools + a verified track record.";

  useEffect(() => {
    document.body.style.background = "#070707";
    return () => { document.body.style.background = ""; };
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press to copy the link");
    }
  };

  const scrollToForm = () => {
    goToAccount();
  };

  return (
    <div className="min-h-screen w-full bg-[#070707]" data-testid="join-funnel">
      {/* ════════ HERO · dark ════════ */}
      <section className="relative overflow-hidden px-4 pt-12 pb-16 sm:px-6 sm:pt-16 sm:pb-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.025]"
          style={{ backgroundImage: "linear-gradient(#BACD1C 1px, transparent 1px), linear-gradient(90deg, #BACD1C 1px, transparent 1px)", backgroundSize: "52px 52px" }}
        />
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-5xl"
        >
          <div className="relative border border-white/10 bg-black">
            <CornerFrame />

            {/* window chrome */}
            <div className="flex items-center gap-2 border-b border-white/10 bg-[#0c0c0c] px-4 py-3">
              <span className="h-3 w-3 rounded-full bg-[#BACD1C]" />
              <span className="h-3 w-3 rounded-full bg-white/70" />
              <span className="h-3 w-3 rounded-full bg-red-500" />
              <span className="ml-2 truncate font-mono text-[10px] uppercase tracking-[0.22em] text-white/35">
                founder@tradrquant: ~/reserve-seat
              </span>
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#BACD1C] opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#BACD1C]" />
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#BACD1C]">online</span>
              </span>
            </div>

            {status === "success" ? (
              <div className="p-6 sm:p-10" data-testid="funnel-success">
                <div className="font-mono text-[13px] leading-relaxed text-white/45 space-y-1.5">
                  <p><span className="text-[#BACD1C]">$</span> ./reserve_seat --email</p>
                  <p className="text-white/35">&gt; validating……</p>
                  <p><span className="text-[#BACD1C]">&gt;</span> {dup ? "already_registered" : "seat_reserved"} <span className="text-[#BACD1C]">[OK]</span></p>
                  <p className="text-white/35">&gt; welcome_email dispatched to inbox</p>
                </div>

                <h2 className="mt-7 font-display text-3xl font-bold uppercase tracking-tight text-[#ededea] sm:text-4xl">
                  {dup ? "You're already in" : "Seat reserved"}
                </h2>
                <p className="mt-3 max-w-xl font-mono text-sm leading-relaxed text-white/50">
                  {dup
                    ? "This email is already on the founder waitlist. Watch your inbox — access opens soon."
                    : "You're on the founder waitlist. Check your inbox for a welcome email — we'll message you the moment early access opens."}
                </p>

                <div className="mt-8 max-w-xl border border-[#BACD1C]/20 bg-[#BACD1C]/[0.03] p-5" data-testid="funnel-share">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#BACD1C]/80">// jump_the_line</p>
                  <p className="mt-2 font-mono text-xs leading-relaxed text-white/45">
                    Share your link — every trader who joins through it is tracked to you and moves you up the queue.
                  </p>
                  <div className="mt-3 flex items-stretch border border-white/12 bg-[#070707]">
                    <span className="min-w-0 flex-1 truncate px-3 py-2.5 font-mono text-xs text-[#BACD1C]" data-testid="funnel-share-link">
                      {shareLink}
                    </span>
                    <button
                      onClick={copyLink}
                      data-testid="funnel-copy-link"
                      className="shrink-0 border-l border-white/12 px-3 text-white/50 transition-colors hover:bg-[#BACD1C]/10 hover:text-[#BACD1C]"
                      aria-label="Copy share link"
                    >
                      {copied ? <Check size={15} /> : <Copy size={15} />}
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareLink)}`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid="funnel-share-x"
                      className="inline-flex items-center justify-center gap-1.5 bg-[#BACD1C] py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] text-black transition-colors hover:bg-white"
                    >
                      <Share2 size={13} /> Share on X
                    </a>
                    <a
                      href={`https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(shareText)}`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid="funnel-share-telegram"
                      className="inline-flex items-center justify-center gap-1.5 border border-[#BACD1C]/35 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] text-[#BACD1C] transition-colors hover:bg-[#BACD1C]/10"
                    >
                      <Share2 size={13} /> Telegram
                    </a>
                  </div>
                </div>

                <a
                  href="/"
                  data-testid="funnel-explore"
                  className="mt-6 inline-block font-mono text-xs uppercase tracking-[0.15em] text-white/40 underline-offset-4 transition-colors hover:text-[#BACD1C] hover:underline"
                >
                  &gt; explore the free tools
                </a>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2">
                {/* left: boot-log pitch */}
                <div className="border-b border-white/[0.06] p-6 sm:p-9 lg:border-b-0 lg:border-r">
                  <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#BACD1C]/80">// founder_access</p>

                  <div className="mt-5 space-y-1.5 font-mono text-[12px] leading-relaxed text-white/40">
                    {BOOT.map(([k, v, s]) => (
                      <p key={k}>
                        <span className="text-[#BACD1C]/70">{k}</span>{" "}
                        <span className="text-white/55">{v}</span>{" "}
                        <span className="text-[#BACD1C]">[{s}]</span>
                      </p>
                    ))}
                  </div>

                  <a
                    href="https://alpha.tradrquant.com/terminal"
                    data-testid="funnel-terminal-banner"
                    className="group mt-6 flex items-center gap-2.5 border border-white/[0.10] bg-white/[0.03] px-3.5 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-white/60 transition-colors hover:border-white/25 hover:text-[#ededea]"
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#BACD1C] animate-pulse" />
                    <span>New · Free Order Flow terminal is live</span>
                    <ArrowRight size={13} className="ml-auto shrink-0 transition-transform group-hover:translate-x-0.5" />
                  </a>

                  <h1 className="mt-7 font-display text-3xl font-bold uppercase leading-[1.0] tracking-tight text-[#ededea] sm:text-4xl">
                    The pro terminal.
                    <br />
                    <span className="text-[#BACD1C]">Free where they charge.</span>
                  </h1>
                  <p className="mt-4 max-w-md font-mono text-[13px] leading-relaxed text-white/45">
                    Footprint, Smart Tape, CVD, whale flow and a verified ledger in one workspace built
                    for retail. The order flow terminal is already free. Get in before founder pricing closes.
                  </p>

                  <div className="mt-7 flex flex-wrap gap-x-7 gap-y-3">
                    {STATS.map(([v, l]) => (
                      <div key={l} data-testid={`funnel-stat-${v}`}>
                        <div className="font-display text-2xl font-bold text-[#e6e6e1]">{v}</div>
                        <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white/35">{l}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 space-y-3.5">
                    {BENEFITS.map(({ icon: Icon, t, d }) => (
                      <div key={t} className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center border border-[#BACD1C]/25 bg-[#BACD1C]/[0.05] text-[#BACD1C]">
                          <Icon size={13} />
                        </span>
                        <div>
                          <p className="font-mono text-[12px] text-white/70">
                            <span className="text-[#BACD1C]/80">[✓]</span> {t}
                          </p>
                          <p className="mt-0.5 font-mono text-[11.5px] leading-relaxed text-white/35">{d}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex items-center gap-2.5 border-t border-white/[0.06] pt-5">
                    <ShieldCheck size={14} className="shrink-0 text-[#BACD1C]" />
                    <p className="font-mono text-[11px] tracking-wide text-white/45">
                      evidence over hype — <span className="text-white/70">we publish the losses too.</span>
                    </p>
                  </div>
                </div>

                {/* right: terminal signup prompt */}
                <div className="flex flex-col justify-center p-6 sm:p-9">
                  <div className="inline-flex w-fit items-center gap-2 border border-[#BACD1C]/25 bg-[#BACD1C]/[0.04] px-3 py-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#BACD1C]" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#BACD1C]/85">early access — filling up</span>
                  </div>

                  <h2 className="mt-5 font-display text-3xl font-bold uppercase leading-[1.02] tracking-tight text-[#ededea] sm:text-[2.4rem]">
                    Claim your
                    <br />founder seat
                  </h2>
                  <p className="mt-3 font-mono text-[13px] leading-relaxed text-white/45">
                    Create your free TradrQuant account to open the terminal and lock in founder pricing, plus your
                    free indicator and alert channels today.
                  </p>

                  <a href={accountUrl()} data-testid="funnel-submit"
                    className="mt-7 inline-flex w-full items-center justify-center gap-2 bg-[#BACD1C] px-6 py-4 font-mono text-sm uppercase tracking-[0.14em] text-black transition-[background-color,transform] duration-300 hover:bg-white active:scale-[0.98]">
                    [ create free account ] <ArrowRight size={16} />
                  </a>

                  <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 border-t border-white/[0.06] pt-5">
                    {["free indicator", "whale alerts", "verified ledger", "academy"].map((c) => (
                      <span key={c} className="inline-flex items-center gap-1.5 font-mono text-[11px] text-white/45" data-testid={`funnel-chip-${c}`}>
                        <Check size={12} className="text-[#BACD1C]" /> {c}
                      </span>
                    ))}
                  </div>

                  <p className="mt-5 font-mono text-[11px] leading-relaxed text-white/30">
                    Not financial advice. Crypto trading carries risk.
                  </p>
                </div>
              </div>
            )}

            {/* status bar */}
            <div className="flex items-center gap-2 border-t border-white/10 bg-[#0c0c0c] px-4 py-3">
              <span className="font-mono text-[11px] tracking-[0.08em] text-white/40">
                <span className="text-[#BACD1C]">&gt;</span> {status === "success" ? "session_complete" : "awaiting_input"}
              </span>
              <span className="ml-0.5 inline-block h-3.5 w-[7px] animate-pulse bg-[#BACD1C]" />
              <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.22em] text-white/25">crypto · retail-first</span>
            </div>
          </div>
        </motion.div>
      </section>

      {status !== "success" && (
        <>
          {/* ════════ keyword marquee ════════ */}
          <div className="w-full overflow-hidden border-y border-white/10 bg-black py-3.5" data-testid="funnel-marquee">
            <div className="flex w-max animate-marquee-slow whitespace-nowrap">
              {[...MARQUEE, ...MARQUEE].map((k, i) => (
                <span key={i} className="flex items-center font-mono text-[11px] uppercase tracking-[0.28em] text-white/30">
                  <span className="mx-6 h-1 w-1 rounded-full bg-[#BACD1C]/70" />
                  {k}
                </span>
              ))}
            </div>
          </div>

          {/* ════════ SOFT WHITE · full-width ════════ */}
          <section className="relative w-full overflow-hidden border-y border-black/5 bg-[#e6e5dd] text-[#0b0b0b]" data-testid="funnel-lightband">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-multiply"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
            />
            <div className="relative mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
              {/* how it works */}
              <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
                <div className="flex items-center gap-2.5">
                  <span className="h-2 w-2 bg-[#BACD1C]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-black/45">how_it_works</span>
                </div>
                <h2 className="mt-5 max-w-3xl font-display text-4xl font-bold uppercase leading-[0.98] tracking-tight sm:text-5xl">
                  From signup to signal in three steps.
                </h2>
              </motion.div>

              <div className="mt-14 grid gap-px overflow-hidden border border-black/10 bg-black/10 sm:grid-cols-3">
                {STEPS.map(([n, t, d], i) => (
                  <motion.div
                    key={n}
                    custom={i}
                    variants={fade}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-60px" }}
                    className="group bg-[#e6e5dd] p-8 transition-colors duration-300 hover:bg-[#f2f1ea]"
                  >
                    <div className="font-display text-5xl font-bold tabular-nums text-black/12 transition-colors duration-300 group-hover:text-[#BACD1C]">{n}</div>
                    <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight">{t}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-black/55">{d}</p>
                  </motion.div>
                ))}
              </div>

              {/* stat strip */}
              <motion.div
                variants={fade}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className="mt-16 grid grid-cols-3 divide-x divide-black/10 border-y border-black/10"
              >
                {STATS.map(([v, l]) => (
                  <div key={l} className="px-4 py-8 text-center">
                    <div className="font-display text-4xl font-bold tracking-tight sm:text-5xl"><CountUp value={v} /></div>
                    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">{l}</div>
                  </div>
                ))}
              </motion.div>

              {/* live now — external tool links */}
              <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mt-20">
                <div className="flex items-center gap-2.5">
                  <span className="h-2 w-2 bg-[#BACD1C]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-black/45">live_now</span>
                </div>
                <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold uppercase leading-[1.0] tracking-tight sm:text-4xl">
                  Don't take our word. Go look.
                </h2>
                <p className="mt-3 max-w-xl font-mono text-[12.5px] leading-relaxed text-black/50">
                  Every one of these is already live and free — no signup required. Open them in a new tab.
                </p>
                <div className="mt-8 grid gap-px overflow-hidden border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-5">
                  {TOOLS.map((t) => (
                    <a
                      key={t.tag}
                      href={t.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`funnel-tool-${t.tag}`}
                      className="group flex flex-col bg-[#e6e5dd] p-6 transition-colors duration-300 hover:bg-white"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/40">{t.tag}</span>
                        <ExternalLink size={14} className="text-black/30 transition-colors duration-300 group-hover:text-black/70" />
                      </div>
                      <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-tight">{t.name}</h3>
                      <p className="mt-2 flex-1 font-mono text-[11.5px] leading-relaxed text-black/50">{t.desc}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-black/70 transition-colors group-hover:text-black">
                        open <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                      </span>
                    </a>
                  ))}
                </div>
              </motion.div>

              {/* price anchor */}
              <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mt-20">
                <div className="flex items-center gap-2.5">
                  <span className="h-2 w-2 bg-[#BACD1C]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-black/45">free_where_they_charge</span>
                </div>
                <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold uppercase leading-[1.0] tracking-tight sm:text-4xl">
                  The same edge. Without the paywall.
                </h2>
                <div className="mt-8 border border-black/10 bg-white/40">
                  {COMPARE.map(([name, elsewhere, tq], i) => (
                    <div
                      key={name}
                      className={`grid grid-cols-[1fr_auto_auto] items-center gap-4 px-5 py-4 transition-colors duration-200 hover:bg-white sm:gap-10 sm:px-7 sm:py-5 ${i ? "border-t border-black/10" : ""}`}
                    >
                      <span className="font-mono text-[12.5px] text-black/70 sm:text-sm">{name}</span>
                      <span className="font-mono text-[12px] text-black/35 line-through decoration-black/25">{elsewhere}</span>
                      <span className="inline-flex min-w-[76px] justify-end">
                        <span className="border border-[#BACD1C] bg-[#BACD1C] px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-black">{tq}</span>
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 font-mono text-[10px] leading-relaxed text-black/30">
                  Price ranges reflect commonly advertised rates for comparable retail tools — illustrative, not a specific competitor quote.
                </p>
              </motion.div>

              {/* proof */}
              <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mt-20">
                <div className="flex items-center gap-2.5">
                  <span className="h-2 w-2 bg-[#BACD1C]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-black/45">on_the_record</span>
                </div>
                <div className="mt-8 grid gap-5 sm:grid-cols-3">
                  {QUOTES.map(([q, u], i) => (
                    <motion.figure
                      key={u}
                      custom={i}
                      variants={fade}
                      initial="hidden"
                      whileInView="show"
                      viewport={{ once: true, margin: "-40px" }}
                      className="flex flex-col border border-black/10 bg-white p-6 shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_14px_34px_-14px_rgba(0,0,0,0.28)]"
                    >
                      <blockquote className="flex-1 text-[15px] leading-relaxed text-black/75">"{q}"</blockquote>
                      <figcaption className="mt-5 font-mono text-[11px] uppercase tracking-[0.16em] text-black/40">{u}</figcaption>
                    </motion.figure>
                  ))}
                </div>
                <p className="mt-5 font-mono text-[10px] leading-relaxed text-black/30">
                  Trader feedback shared with permission. Not a promise of performance — trading carries risk.
                </p>
              </motion.div>
            </div>
          </section>

          {/* ════════ DARK · FAQ + close ════════ */}
          <section className="w-full px-4 py-20 sm:px-6 sm:py-28">
            <div className="mx-auto max-w-3xl">
              <div className="flex items-center gap-2.5">
                <span className="h-2 w-2 bg-[#BACD1C]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/40">faq</span>
              </div>
              <div className="mt-8 divide-y divide-white/[0.07] border-y border-white/[0.07]" data-testid="funnel-faq">
                {FAQ.map(([q, a]) => (
                  <div key={q} className="py-6">
                    <p className="font-mono text-[14px] text-white/80">
                      <span className="text-[#BACD1C]">&gt;</span> {q}
                    </p>
                    <p className="mt-2.5 font-mono text-[12.5px] leading-relaxed text-white/45">{a}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mx-auto mt-16 max-w-3xl border border-[#BACD1C]/25 bg-[#BACD1C]/[0.03] p-9 text-center sm:p-12" data-testid="funnel-close">
              <div className="mx-auto inline-flex items-center gap-2 border border-[#BACD1C]/30 bg-[#BACD1C]/[0.06] px-3 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#BACD1C]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#BACD1C]/85">founder pricing · closing at launch</span>
              </div>
              <p className="mt-5 font-display text-3xl font-bold uppercase leading-[1.05] tracking-tight text-[#ededea] sm:text-4xl">
                Lock in founder pricing<br className="hidden sm:block" /> <span className="text-[#BACD1C]">before it's gone for good.</span>
              </p>
              <p className="mx-auto mt-4 max-w-xl font-mono text-[13px] leading-relaxed text-white/50">
                Free account holders claim the full terminal at the lowest price it will ever be — and keep it.
                The moment TradrQuant opens to the public, introductory founder pricing closes permanently.
                Miss this window and you pay full price later.
              </p>
              <button
                type="button"
                onClick={scrollToForm}
                data-testid="funnel-close-cta"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap bg-[#BACD1C] px-5 py-4 font-mono text-xs uppercase tracking-[0.1em] text-black transition-[background-color,transform] duration-300 hover:bg-white active:scale-[0.98] sm:w-auto sm:px-8 sm:text-sm sm:tracking-[0.14em]"
              >
                [ create free account ]
                <ArrowRight size={16} className="shrink-0" />
              </button>
              <p className="mt-4 font-mono text-[11px] text-white/30">Free account · no card required.</p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
